<template>
  <portal to="modal-windows">
    <AppDialog
      :density="DIALOG_DENSITY.COMPACT"
      :show="show"
      :title="$t('action.share')"
      hide-cancel-button
      hide-hero
      @on-confirm="close"
      @on-close="close"
    >
      {{ $t('common.copy_link_description') }}

      <CopyLinksList :links-list="linksForShare" />

      <template #confirm-btn-text>
        {{ $t('action.done') }}
      </template>
    </AppDialog>
  </portal>
</template>

<script setup>
import { Portal } from 'portal-vue'
import { computed, inject } from 'vue'
import { useStore } from 'vuex'

import { ROUTE_PATHS } from '@/routes/route-helpers'
import { DIALOG_DENSITY } from '@/utils/components-configurations/app-dialog'
import {
  composeForgeAppUrl,
  composeUrlSegments,
  getHashFromLocation,
  replaceAzureLocationHash,
  replaceForgeLocationHash
} from '@/utils/external-link-helper'
import { isJiraAppInjectionKey, isWebAppInjectionKey } from '@/utils/injection-keys'
import { getAtlassianBaseUrl } from '@jira/util'

import AppDialog from '@/components/AppDialog'
import { useLinksList } from '@/components/ui/CopySharedLinkModal/composables'
import CopyLinksList from '@/components/ui/CopySharedLinkModal/CopyLinksList'

defineOptions({
  name: 'CopySharedLinkModal'
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },

  token: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({ 'update:show': null })

const targetPath = computed(() => `/${ROUTE_PATHS.SHARE}/${props.token}`)

const isJiraApp = inject(isJiraAppInjectionKey)
const isWebApp = inject(isWebAppInjectionKey)

const store = useStore()

const getFallbackPath = () => {
  const hash = getHashFromLocation() && `/${getHashFromLocation()}`

  return `${location.origin}${hash}${targetPath.value}`
}

const generateLinkToJira = async () => {
  if (isJiraApp) {
    const result = await replaceForgeLocationHash({ hash: targetPath.value })

    return result || getFallbackPath()
  }

  if (isWebApp) {
    return composeForgeAppUrl({
      siteUrl: getAtlassianBaseUrl(),
      hash: targetPath.value
    })
  }

  return getFallbackPath()
}

const webAppUrl = computed(() => store.state.appContext.urls.webAppUrl)

const generateLinkToWebApp = async () => {
  if (isJiraApp) {
    return composeUrlSegments(webAppUrl.value, '#', targetPath.value)
  }

  if (isWebApp) {
    const hash = getHashFromLocation() && `/${getHashFromLocation()}`

    return `${location.origin}${hash}${targetPath.value}`
  }

  return getFallbackPath()
}

const generateLinkToAzureApp = async () => {
  const result = await replaceAzureLocationHash({ hash: targetPath.value })

  return result || getFallbackPath()
}

const { linksForShare } = useLinksList({
  jiraLinkGenerator: generateLinkToJira,
  webAppLinkGenerator: generateLinkToWebApp,
  azureAppLinkGenerator: generateLinkToAzureApp
})

const close = () => {
  emit('update:show', false)
}
</script>

<style lang="scss" scoped></style>
